import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Link, tokens, Tooltip, CopyAction, Text } from '@hotjar/hotsauce';
import { PollUpsell } from '@hotjar/webapp/components/PollUpsell/PollUpsell';
import { sanitizeUUID, sanitize } from '@hotjar/webapp/lib/sanitize';
import type { IconSetDefinitions } from '@hotjar/hotsauce/build/components/Icon/icons';
import { capitalize } from '@hotjar/insights-common/lib/string';

import { epochDateTime, timeAgo } from '@hotjar/webapp/lib/time';
import { moment } from '@hotjar/webapp/lib/moment';

import { transformBrowserNameToIcon } from '@hotjar/recordings/src/utils/transformBrowserNameToIcon';
import { transformOsNameToIcon } from '@hotjar/recordings/src/utils/transformOsNameToIcon';
import { getCountryNameFromCode } from '@hotjar/country-lib';
import type { CountryCode } from '@hotjar/country-lib';

import type { Site as SiteModel } from '@hotjar/site';
import type { Site } from '@hotjar/webapp/domains/common/feature';
import type { ActionFilter } from '@hotjar/recordings/src/features/PlayerPage/components/EventBoxHolder/types';
import type { Recording, EventMarker } from '../types';

import { UserAttributes } from './UserAttributes';
import { ErrorReportingTile } from './ErrorReportingTile';
import { UserActionsRow } from './UserActionsRow';
import {
  Row,
  IconSpaced,
  Container,
  DivOverflow,
  Flag,
  Unknown,
  ScrollContainer,
  Title,
} from './styles';
import { SimilarRecordingsSection } from './SimilarRecordingsSection/SimilarRecordingsSection';

const getRecordingsFilterByUUIDURL = (recording: Recording): string => {
  const visitorUUID = recording && recording.visitor_uuid;
  const jsonFilters = {
    AND: [
      {
        DAYS_AGO: {
          created: 365,
        },
      },
      {
        EQUAL: {
          visitor_uuid: sanitizeUUID(visitorUUID),
        },
      },
    ],
  };

  return `/sites/${recording.site_id}/playbacks/list?filters=${encodeURI(
    JSON.stringify(jsonFilters),
  )}`;
};

export const UserIdInfo = ({ recording, asLink }: Props & { asLink: boolean }): JSX.Element => {
  const { t } = useTranslation();
  const sanitizedUUID = sanitizeUUID(recording.visitor_uuid);

  return (
    <>
      <IconSpaced icon="user_single" description="user" />
      <DivOverflow>
        {asLink ? (
          <>
            <span>{t('common:player.session-info.hj-user-id')}:</span>
            &nbsp;
            <Link
              kind="inline"
              isExternal
              data-hj-suppress
              href={getRecordingsFilterByUUIDURL(recording)}
            >
              {sanitizedUUID}
            </Link>
          </>
        ) : (
          <Text textType="caption" data-hj-suppress>
            {sanitizedUUID}
          </Text>
        )}

        {recording.is_first_seen && (
          <span>
            {' - '}
            <Text textType="caption" textColor="text_light">
              {t('common:player.session-info.new-user')}
            </Text>
          </span>
        )}
      </DivOverflow>
    </>
  );
};

const UserId = ({ recording }: Props): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Tooltip placement="left" text={t('common:player.session-info:hj-user-id')}>
      <Row>
        <UserIdInfo recording={recording} asLink />
      </Row>
    </Tooltip>
  );
};

export const CountryInfo = ({ recording }: Props): JSX.Element => {
  return (
    <>
      <Flag code={recording.country_code} />
      <DivOverflow data-hj-suppress>
        {recording.country_code
          ? getCountryNameFromCode(recording.country_code.toUpperCase() as CountryCode)
          : ''}
      </DivOverflow>
    </>
  );
};

const Country = ({ recording }: Props): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      {recording.country_name && (
        <Tooltip placement="left" text={t('common:player.session-info.country')}>
          <Row>
            <CountryInfo recording={recording} />
          </Row>
        </Tooltip>
      )}
      {!recording.country_name && (
        <Row>
          <Flag code={recording.country_code} />
          <Unknown>{t('common:player.session-info.unknown-country')}</Unknown>
        </Row>
      )}
    </>
  );
};

function getRecordingTime(epochTime: number): string {
  const epochTimeMs = epochTime * 1000; // Convert the seconds to milliseconds

  const momentizedDate = moment(epochTimeMs);

  const format = 'MMM D, HH:mm';

  return momentizedDate.format(format);
}

const TimeAgo = ({ recording }: Props): JSX.Element => (
  <Tooltip placement="left" text={epochDateTime(recording.created_epoch_time)}>
    <Row>
      <IconSpaced icon="date" description="calendar" />
      <DivOverflow data-hj-suppress>
        {timeAgo(recording.created_epoch_time, Infinity)} -{' '}
        {getRecordingTime(recording.created_epoch_time)}
      </DivOverflow>
    </Row>
  </Tooltip>
);

export const DeviceInfo = ({ recording }: Props): JSX.Element => {
  const icon = `device_${recording.device}` as unknown as keyof IconSetDefinitions;

  return (
    <>
      <IconSpaced icon={icon} description="device" />
      <DivOverflow data-hj-suppress>
        {capitalize(recording.device)} ({recording.window_size[0]} x {recording.window_size[1]})
      </DivOverflow>
    </>
  );
};

const Device = ({ recording }: Props): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Tooltip placement="left" text={t('common:player.session-info.device')}>
      <Row>
        <DeviceInfo recording={recording} />
      </Row>
    </Tooltip>
  );
};

export const BrowserInfo = ({ recording }: Props): JSX.Element => {
  const icon = transformBrowserNameToIcon(recording.browser);

  return (
    <>
      <IconSpaced
        icon={icon}
        color={icon === 'unknown' ? 'text_light' : 'inherited'}
        description="browser"
      />
      <DivOverflow data-hj-suppress>{recording.browser}</DivOverflow>
    </>
  );
};

const Browser = ({ recording }: Props): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Tooltip placement="left" text={t('common:player.session-info.browser')}>
      <Row>
        <BrowserInfo recording={recording} />
      </Row>
    </Tooltip>
  );
};

export const OSInfo = ({ recording }: Props): JSX.Element => {
  const icon = transformOsNameToIcon(recording.os);

  return (
    <>
      <IconSpaced
        icon={icon}
        color={icon === 'unknown' ? 'text_light' : 'inherited'}
        description="Operating system"
      />
      <DivOverflow data-hj-suppress>{recording.os}</DivOverflow>
    </>
  );
};

const OS = ({ recording }: Props): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Tooltip placement="left" text={t('common:player.session-info.operating-system')}>
      <Row>
        <OSInfo recording={recording} />
      </Row>
    </Tooltip>
  );
};

export const ReferrerInfo = ({ recording }: Props): JSX.Element => {
  return (
    <>
      <IconSpaced icon="forward" description="referrer" />
      <DivOverflow data-hj-suppress>{recording.referrer}</DivOverflow>
    </>
  );
};

const Referrer = ({ recording }: Props): JSX.Element => {
  const { t } = useTranslation();

  return recording.referrer ? (
    <Row
      css={css`
        /**
         * To fully show the outline - for accessibility reason - when the copy button is clicked,
         * we need to make the overflow of the content visible, so we won't touch the Row's styles.
         */
        overflow: visible;
      `}
    >
      <Tooltip
        placement="left"
        text={
          (
            <span>
              {t('common:player.session-info.referrer-url')}:
              <br /> {sanitize(recording.referrer)}
            </span>
          ) as unknown as string
        }
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            max-width: 90%;
          `}
        >
          <ReferrerInfo recording={recording} />
        </div>
      </Tooltip>
      <CopyAction
        toCopy={recording.referrer}
        description="Copy referrer"
        onCopy={() => {
          //
        }}
      />
    </Row>
  ) : (
    <>
      <IconSpaced color="text_light" icon="forward" description="referrer" />
      <Unknown>{t('common:player.session-info.unknown-referrer')}</Unknown>
    </>
  );
};

type Props = {
  recording: Recording;
};

type ExtraProps = {
  site: Site | null;
  sidePanelEventMarkers: EventMarker[];
  onActionClick: (type?: ActionFilter) => void;
};

type PollUpsell = {
  onCreatePollClick: () => void;
  showPollUpsell: boolean;
};

export type SessionInfoProps = Props & PollUpsell & ExtraProps;

export const SessionInfo = ({
  recording,
  site,
  sidePanelEventMarkers,
  onActionClick,
  onCreatePollClick,
  showPollUpsell,
}: SessionInfoProps): JSX.Element | null => {
  const { t } = useTranslation();

  const isSimilarRecordingsSectionAvailable = useMemo(
    () => recording && recording.effective_plan !== 'basic',
    [recording],
  );

  if (!recording) {
    return null;
  }

  return (
    <ScrollContainer>
      {site ? <ErrorReportingTile site={site as SiteModel} /> : null}
      <UserActionsRow eventMarkers={sidePanelEventMarkers} onActionClick={onActionClick} />
      <Container>
        <Title>{t('common:player.side-panel.session-info.title')}</Title>
        <UserId recording={recording} />
        <Country recording={recording} />

        <TimeAgo recording={recording} />
        <Device recording={recording} />
        <Browser recording={recording} />
        <OS recording={recording} />
        <Referrer recording={recording} />
      </Container>

      {isSimilarRecordingsSectionAvailable && <SimilarRecordingsSection />}
      <UserAttributes recording={recording} />

      {showPollUpsell && (
        <PollUpsell
          css={css`
            margin-top: ${tokens.spacer.x2};
          `}
          onCreatePollClick={onCreatePollClick}
        />
      )}
    </ScrollContainer>
  );
};
